import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQueryClient} from '@github-ui/react-query'
import {useCallback} from 'react'

import {fetchConversationCommentUpdates} from '../page-data/loaders/use-conversation-comments'
import {usePullRequestBasePath} from '../stores/layout-selectors'
import {useRespondToAliveUpdate} from './use-refetch-on-alive-update'
import {useLayoutRouteData} from '../page-data/loaders/use-layout-route-data'
import {threadsQueryKey} from '../page-data/loaders/use-threads'

/** Incrementally refreshes loaded conversation comments after timeline updates. */
export function useConversationCommentsLiveUpdates(): void {
  const {aliveChannels} = useLayoutRouteData()
  const channel = aliveChannels?.pullRequest

  const basePath = usePullRequestBasePath()
  const queryClient = useQueryClient()

  const liveUpdatesEnabled = isFeatureEnabled('prs_new_conversation_comments_api_live_updates')

  const fetchComments = useCallback(async () => {
    try {
      const newComments = await fetchConversationCommentUpdates(queryClient, basePath)

      if (newComments.some(comment => comment.type === 'review_summary')) {
        // TODO - follow up with targeted fetching for the review summary's threads
        // this will overfetch but is a temporary solution until we add thread ids to the review summary payload
        await queryClient.invalidateQueries({queryKey: threadsQueryKey(basePath), exact: true})
      }
    } catch {
      // Live updates are non-critical; the next update or page load can reconcile the comments.
    }
  }, [basePath, queryClient])

  useRespondToAliveUpdate(liveUpdatesEnabled ? channel : undefined, fetchComments, {timeline_updated: true})
}
